import React from 'react'
import useI18n from '../../i18n/useI18n'
import './index.scss'
import RoleBasedAccess from '../role-based-access'
import { UserTypeEnum } from '../../utils/enums'
import general from '../../utils/general'

const Timeline = ({ items = [], loading = false, emptyMessage, showActions = true }) => {
  const { t, language } = useI18n()
  const defaultEmptyMessage = t('TIMELINE_EMPTY')
  const activeEmptyMessage = emptyMessage || defaultEmptyMessage

  if (loading) {
    return (
      <div className="timeline-container">
        <div className="timeline-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">{t('COMMON_LOADING')}</span>
          </div>
        </div>
      </div>
    )
  }

  if (!items || items.length === 0) {
    return (
      <div className="timeline-container">
        <div className="timeline-empty">
          <i className="zmdi zmdi-notifications-off"></i>
          <p>{activeEmptyMessage}</p>
        </div>
      </div>
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now - date)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      // Son 7 gün içindeyse "X gün önce" formatı
      if (diffDays <= 7) {
        if (diffDays === 0) return t('TIMELINE_TODAY')
        if (diffDays === 1) return t('TIMELINE_YESTERDAY')
        return t('TIMELINE_DAYS_AGO', { count: diffDays })
      }

      // Son 30 gün içindeyse "X hafta önce"
      if (diffDays <= 30) {
        const weeks = Math.floor(diffDays / 7)
        return t('TIMELINE_WEEKS_AGO', { count: weeks })
      }

      // Daha eski tarihler için tam tarih
      return date.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  // Tarihe göre sırala (en yeni üstte)
  const sortedItems = [...items].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.date || 0)
    const dateB = new Date(b.createdAt || b.date || 0)
    return dateB - dateA
  })

  return (
    <div className="timeline-container">
      <div className="timeline">
        {sortedItems.map((item, index) => (
          <div key={item.id || index} className="timeline-item" style={{ animationDelay: `${index * 0.1}s` }}>
            <div className="timeline-marker">
              <div className="timeline-marker-inner">
                <i className="zmdi zmdi-notifications-active"></i>
              </div>
              <div className="timeline-marker-pulse"></div>
            </div>
            <div className="timeline-content">
              <div className="timeline-header">
                <div className="timeline-title-wrapper">
                  <h4 className="timeline-title">
                    {general.getLocaleValue(item, 'title', language) !== '-'
                      ? general.getLocaleValue(item, 'title', language)
                      : t('TIMELINE_UNTITLED')}
                  </h4>
                  {item.isPublished !== undefined && (
                    <span className={`timeline-status-badge ${item.isPublished ? 'published' : 'draft'}`}>
                      <i className={`zmdi zmdi-${item.isPublished ? 'check-circle' : 'edit'}`}></i>
                      {item.isPublished ? t('TIMELINE_STATUS_PUBLISHED') : t('TIMELINE_STATUS_DRAFT')}
                    </span>
                  )}
                </div>
                <div className="timeline-meta">
                  <span className="timeline-date">
                    <i className="zmdi zmdi-time"></i>
                    {formatDate(item.createdAt || item.date)}
                  </span>
                </div>
              </div>
              {(item.content || item.description) && (
                <div className="timeline-body">
                  <div
                    className="timeline-content-html ck-content-preview"
                    dangerouslySetInnerHTML={{ __html: item.content || item.description }}
                  />
                </div>
              )}
              <div className="timeline-footer">
                <div className="timeline-footer-left">
                  {item.status && (
                    <span className={`badge badge-${item.status === 'active' ? 'success' : 'secondary'}`}>
                      {item.status === 'active' ? t('TIMELINE_STATUS_ACTIVE') : t('TIMELINE_STATUS_INACTIVE')}
                    </span>
                  )}
                  {item.priority !== undefined && item.priority > 0 && (
                    <span className="timeline-priority">
                      <i className="zmdi zmdi-star"></i>
                      {t('TIMELINE_PRIORITY')}: {item.priority}
                    </span>
                  )}
                  {item.link && (
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 11, display: "flex", gap: 5, alignItems: "center", justifyContent: "center" }}
                      className="btn btn-sm btn-info btn-round timeline-link-btn"
                      title={t('TIMELINE_OPEN_LINK') || 'Bağlantıyı Aç'}
                    >
                      <i className="zmdi zmdi-open-in-new"></i>
                      {t('TIMELINE_OPEN_LINK') || 'Bağlantıyı Aç'}
                    </a>
                  )}
                </div>
                {showActions && (item.onEdit || item.onDelete) && (
                  <div className="timeline-footer-actions">
                    {item.onEdit && (
                      <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
                        <button
                          className="btn btn-sm btn-warning btn-icon btn-round timeline-action-btn"
                          onClick={() => item.onEdit(item)}
                          title={t('COMMON_ACTIONS_EDIT')}
                        >
                          <i className="zmdi zmdi-edit"></i>
                        </button>
                      </RoleBasedAccess>
                    )}
                    {item.onDelete && (
                      <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
                        <button
                          className="btn btn-sm btn-danger btn-icon btn-round timeline-action-btn"
                          onClick={() => item.onDelete(item)}
                          title={t('COMMON_ACTIONS_DELETE')}
                        >
                          <i className="zmdi zmdi-delete"></i>
                        </button>
                      </RoleBasedAccess>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default Timeline

